
import { notify,
  clientConfig,
  demoDataFiles,
  demoImages,
  getSdk,
  strings,
} from "@quickpoint.me/shared";
import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";
import { getAuth, getIdToken } from "firebase/auth";
import { GraphQLClient } from "graphql-request";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { AllFiles } from "../state/files";
import { DemoMode } from "../state/uiAtoms";
import { Authorization } from "../state/authAtoms";
import { sendAllFiles, toUploaderBC, updateFiles } from "../lib/messageBus";
import { HostId } from "../state/atoms";
import { OwnerName } from "../state/authSelectors";
import { StackId } from "../state/stackSelectors";
import type { Message } from "@quickpoint.me/shared";

function sortFilesByStackAndName(files: QpFile[], currentStackId: string) {
  return files.sort((a, b) => {
    // Files with currentStackId come first
    const aInStack = a.stackId === currentStackId ? 0 : 1;
    const bInStack = b.stackId === currentStackId ? 0 : 1;
    if (aInStack !== bInStack) {
      return aInStack - bInStack;
    }
    // Then sort alphabetically by fileName (case-insensitive)
    return a.fileName.localeCompare(b.fileName, undefined, {
      sensitivity: "base",
    });
  });
}

export function useFileApi() {
  const hostId = useRecoilValue(HostId);
  const demoMode = useRecoilValue(DemoMode);
  const auth = useRecoilValue(Authorization);
  const currentStackId = useRecoilValue(StackId);
  const ownerName = useRecoilValue(OwnerName);
  const setAllFiles = useSetRecoilState(AllFiles);

  async function fetchAllFiles(): Promise<void> {
    if (demoMode) {
      setAllFiles([...demoImages, ...demoDataFiles]);
      return;
    }

    const client = new GraphQLClient(clientConfig.gqlGetFile());
    const sdk = getSdk(client);
    const user = getAuth().currentUser;
    if (!auth.uid || !user) {
      // auth not ready yet — keep whatever list we already have; wiping it
      // here gets rebroadcast to the fileuploader and empties the modal
      return;
    }

    const jwt = await getIdToken(user);

    try {
      const res = await sdk.GetFiles({ jwt });

      if (!res.getFiles?.files) {
        setAllFiles([]);
        return;
      }

      const sortedFiles = sortFilesByStackAndName(
        res.getFiles.files,
        currentStackId,
      );

      // Broadcast files to anyone listening
      sendAllFiles(hostId, sortedFiles);

      // Update the atom
      setAllFiles(sortedFiles);
    } catch (e) {
      console.error(e);
      notify(
        "There is an error with the server. Please refresh or try again later.",
      );
      setAllFiles([]);
    }
  }

  async function deleteImageFromQP(fileId: string) {
    const client = new GraphQLClient(clientConfig.gqlWriteFile());
    const sdk = getSdk(client);

    const user = getAuth().currentUser;
    if (!user) return;
    const jwt = await getIdToken(user);

    if (ownerName && jwt) {
      const gqlVars = { fileId, jwt };

      sdk
        .DeleteFile(gqlVars)
        .then((res) => {
          if (res.deleteFile?.success) {
            updateFiles(hostId);
          } else {
            if (toUploaderBC) {
              const message: Message = {
                hostId,
                type: "fileUploader",
                action: strings.messages.deleteImageFailed,
                payload: fileId,
              };
              toUploaderBC.postMessage(message);
            }
            updateFiles(hostId);
            notify("There was an error.");
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  return { fetchAllFiles, deleteImageFromQP };
}
